import React from 'react';
import { connect } from 'react-redux';
import { collectAndHydrateTicketsOnSameChannel } from 'actions/ticketActions';
import { previousConversationsClicked } from '../../analytics/mlEventAnalytics';
import { activateHistoricalTicket, deactivateHistoricalTicket } from 'actions/tools/ToolActions';
import * as extractFromState from 'extractFromState';
import * as colors from 'components/common/colorConstants';
import { FadeText } from 'components/common/toggleComponents';
import { formatDate } from 'util/formatUtil';
import {
  selectTicket,
  openPreviousConversationsSection,
  closePreviousConversationsSection,
  setLoadingUnique,
} from '../../actions/index';
import { handleError } from 'services/ErrorService/ErrorService';
import { ToolTip } from 'components/common/tooltips';
import {
  SectionContainer,
  LeftSection,
  RightSection,
  PlaceholderContainer,
  PlaceholderText,
  LoadingDots,
  PreviousConversationSectionRightText,
  PreviousConversationSectionBackIcon,
  PreviousConversationSectionBackToCurrent,
  PreviousConversationSectionLabel,
  PreviousConversationButtonGrid,
  PreviousConversationButton,
  PreviousConversationIcon,
  PreviousConversationButtonLabel,
  PlaceholderDiv,
  CloseContainer,
} from './PreviousConversationSection.styles';
import { setInProgressTreeAndWizardLocation } from 'actions/inProgressTreeAndWizardLocationActions';
import { SELECT_TICKET_LOADING } from 'constants/loading';

export class PreviousConversationSection extends React.Component {
  async componentDidMount() {
    this.props.setLoading(true);
    try {
      await this.props.collectAndHydrateTicketsOnSameChannel();
    } catch (error) {
      handleError({ error, showPopup: true, customMessage: 'There was a problem with loading tickets history' });
    } finally {
      this.props.setLoading(false);
    }
  }

  async componentDidUpdate(prevProps) {
    if (
      this.props.selectedNonHistoricalTicketId &&
      prevProps.selectedNonHistoricalTicketId !== this.props.selectedNonHistoricalTicketId
    ) {
      this.props.setLoading(true);
      try {
        await this.props.collectAndHydrateTicketsOnSameChannel();
      } catch (error) {
        handleError({ error, showPopup: true, customMessage: 'There was a problem with loading tickets history' });
      } finally {
        this.props.setLoading(false);
      }
    }

    /**
     * If we are viewing a previous conversation, it keeps the tool mask active until the
     * selecting of the ticket and rehydrating of the state is complete.
     */
    if (!this.props.selectedHistoricalTicketId && !this.props.isSelectTicketLoading) {
      this.props.deactivateHistoricalTicket();
    }
  }

  renderButton = (ticket, iconColor, selected) => {
    const subject = ticket.subject;

    return (
      <ToolTip key={ticket.ticketId} arrow placement="bottom" content={<div>{subject}</div>}>
        <PreviousConversationButton
          selected={selected}
          onClick={(e) => {
            e.stopPropagation();
            if (!this.props.displayingHistoricalConversation) {
              this.props.setInProgressTreeAndWizardLocation();
            }

            this.props.activateHistoricalTicket();
            this.props.onShowMessageDetails(ticket.ticketId, 'HISTORICAL', true, ticket.isExternalHistoricalTicket);
            previousConversationsClicked({ conversationId: ticket.ticketId });
          }}
        >
          <PreviousConversationIcon src={`/images/previous-ticket-icon-${iconColor}.svg`} />
          <PreviousConversationButtonLabel selected={selected}>
            {formatDate(ticket.createdAt)}
          </PreviousConversationButtonLabel>
        </PreviousConversationButton>
      </ToolTip>
    );
  };

  renderAllTicketsButton = (allTickets) =>
    allTickets.map((ticketId) => {
      const selected = this.props.selectedHistoricalTicketId === ticketId;
      const iconColor = selected ? 'light' : 'dark';

      return this.renderButton(this.props.historicalTickets[ticketId], iconColor, selected);
    });

  renderPreviousConversationsGrid = () => {
    const allTickets = this.props.historicalTickets && this.props.historicalTickets.allTickets;

    return (
      <PreviousConversationButtonGrid>
        {allTickets && this.renderAllTicketsButton(allTickets)}
      </PreviousConversationButtonGrid>
    );
  };

  render() {
    const shouldShowNoPreviousConversationsMessage =
      !this.props.historicalTickets || this.props.historicalTickets.historicalTicketsTotalCount === 0;

    if (this.props.isLoading) {
      return (
        <PlaceholderContainer>
          <FadeText>
            <PlaceholderText>Loading </PlaceholderText>
          </FadeText>
          <LoadingDots color={colors.blue1} size={6} />
        </PlaceholderContainer>
      );
    } else if (shouldShowNoPreviousConversationsMessage) {
      return (
        <PlaceholderContainer>
          <FadeText>
            <PlaceholderText> No previous conversations </PlaceholderText>
          </FadeText>
        </PlaceholderContainer>
      );
    }

    return (
      <SectionContainer>
        <LeftSection>
          <PreviousConversationSectionLabel>
            Previous ({this.props.historicalTickets.historicalTicketsTotalCount})
          </PreviousConversationSectionLabel>
          {this.renderPreviousConversationsGrid()}
        </LeftSection>
        <RightSection>
          <PreviousConversationSectionBackToCurrent
            onClick={(e) => {
              e.stopPropagation();
              this.props.onShowMessageDetails(
                this.props.selectedNonHistoricalTicketId,
                this.props.selectedTab,
                false,
                false
              );
            }}
          >
            {this.props.displayingHistoricalConversation ? (
              <>
                <ToolTip arrow placement="bottom" content="Back to conversation">
                  <PreviousConversationSectionRightText>Back</PreviousConversationSectionRightText>
                </ToolTip>
                <PreviousConversationSectionBackIcon alt="hide" src="/images/back-current-conversation-icon.svg" />
              </>
            ) : (
              <PlaceholderDiv />
            )}
            {
              <CloseContainer>
                <img
                  alt="hide"
                  src="/images/arrows/up-arrow-gray.svg"
                  onClick={() => {
                    this.props.closePreviousConversationsSection();
                    this.props.onShowMessageDetails(
                      this.props.selectedNonHistoricalTicketId,
                      this.props.selectedTab,
                      false,
                      false
                    );
                  }}
                />
              </CloseContainer>
            }
          </PreviousConversationSectionBackToCurrent>
        </RightSection>
      </SectionContainer>
    );
  }
}

const mapStateToProps = (state) => {
  return {
    selectedHistoricalTicketId: extractFromState.extractSelectedHistoricalTicketIdFromState(state),
    selectedTab: extractFromState.extractSelectedInboxTab(state),
    selectedNonHistoricalTicketId: extractFromState.extractNonHistoricalTicketIdFromState(state),
    displayingHistoricalConversation: extractFromState.extractDisplayingHistoricalConversationFromState(state),
    historicalTickets: extractFromState.extractHistoricalTickets(state),
    isLoading: extractFromState.isLoadingUnique(state, 'PreviousConversationSection'),
    isSelectTicketLoading: extractFromState.isLoadingUnique(state, SELECT_TICKET_LOADING),
  };
};

const mapDispatchToProps = (dispatch) => {
  return {
    setLoading(val) {
      dispatch(setLoadingUnique(val, 'PreviousConversationSection'));
    },
    activateHistoricalTicket() {
      dispatch(activateHistoricalTicket());
    },
    deactivateHistoricalTicket() {
      dispatch(deactivateHistoricalTicket());
    },
    onShowMessageDetails: (ticketId, tab, isHistoricalTicket, isExternalHistoricalTicket) => {
      dispatch(selectTicket(ticketId, tab, isHistoricalTicket, isExternalHistoricalTicket, false));
    },
    collectAndHydrateTicketsOnSameChannel: () => {
      return dispatch(collectAndHydrateTicketsOnSameChannel());
    },
    openPreviousConversationsSection() {
      dispatch(openPreviousConversationsSection());
    },
    setInProgressTreeAndWizardLocation() {
      dispatch(setInProgressTreeAndWizardLocation());
    },
    closePreviousConversationsSection() {
      dispatch(closePreviousConversationsSection());
    },
  };
};

export default connect(mapStateToProps, mapDispatchToProps)(PreviousConversationSection);
